import React, { useState, useRef } from 'react';
import EmailVerification from './EmailVerification';
import IDVerification from './IDVerification';
import RegisterCheckbox from './RegisterCheckbox';
import CertiUploader from './CertiUploader';
import '../styles/components/MarketerRegistForms.css';

const API_URL = process.env.REACT_APP_API_URL;

function MarketerRegistAgencyForm({
  formData, setFormData,
  emailVerified, setEmailVerified,
  idVerified, setIdVerified,
  requiredAgreed, setRequiredAgreed,
  loading, setLoading,
  setMessage,
  setIsSuccessModalOpen,
  passRe = /^[\]A-Za-z0-9~`!@#$%^&*()_\-+={}[|\\:;"'<>,.?/]{8,20}$/
}) {
  const [companyName, setCompanyName] = useState('');
  const [position, setPosition] = useState('');
  const [bizFiles, setBizFiles] = useState([]);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const nameRef = useRef(null);
  const callNumberRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const companyNameRef = useRef(null);
  const positionRef = useRef(null);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const setFieldError = (key, msg, action) => {
    setFieldErrors(prev => ({ ...prev, [key]: msg }));
    if (action) setTimeout(() => action(), 0);
  };

  const clearFieldErrors = () => setFieldErrors({});

  const uploadWithRetry = async (fd, url, { max = 3, baseDelay = 800, timeoutMs = 15000 } = {}) => {
    for (let i = 0; i < max; i++) {
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), timeoutMs);
      try {
        const res = await fetch(url, { method: 'POST', body: fd, signal: ac.signal });
        clearTimeout(timer);
        if (res.ok) return true;
      } catch {}
      const delay = baseDelay * (2 ** i) + Math.random() * 300;
      await new Promise(r => setTimeout(r, delay));
    }
    return false;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFieldErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearFieldErrors();
    setMessage('');

    if (!formData.userName.trim()) {
      setFieldError('userName', '이름을 입력해주세요.', () => nameRef.current?.focus());
      return;
    }
    if (!formData.callNumber.trim()) {
      setFieldError('callNumber', '연락처를 입력해주세요.', () => callNumberRef.current?.focus());
      return;
    }
    if (!formData.email.trim()) {
      setFieldError('email', '이메일을 입력해주세요.', () => scrollTo('agency-section-email'));
      return;
    }
    if (!emailVerified) {
      setFieldError('email', '이메일 인증을 완료해주세요.', () => scrollTo('agency-section-email'));
      return;
    }
    if (!formData.userId.trim()) {
      setFieldError('userId', '아이디를 입력해주세요.', () => scrollTo('agency-section-id'));
      return;
    }
    if (!idVerified) {
      setFieldError('userId', '아이디 중복검사를 완료해주세요.', () => scrollTo('agency-section-id'));
      return;
    }
    if (!formData.userPassword.trim()) {
      setFieldError('userPassword', '비밀번호를 입력해주세요.', () => passwordRef.current?.focus());
      return;
    }
    if (!passRe.test(String(formData.userPassword))) {
      setFieldError('userPassword', '영문/숫자/특수기호 조합 8~20자, 공백 불가', () => passwordRef.current?.focus());
      return;
    }
    if (formData.userPassword !== confirmPassword) {
      setFieldError('confirmPassword', '비밀번호가 일치하지 않습니다.', () => confirmPasswordRef.current?.focus());
      return;
    }
    if (!companyName.trim()) {
      setFieldError('companyName', '회사명을 입력해주세요.', () => companyNameRef.current?.focus());
      return;
    }
    if (!position.trim()) {
      setFieldError('position', '직급을 입력해주세요.', () => positionRef.current?.focus());
      return;
    }
    if (!requiredAgreed) {
      setFieldError('terms', '필수 약관에 동의해주세요.', () => scrollTo('agency-section-terms'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type: 'agency',
        userName: formData.userName,
        userId: formData.userId,
        userPassword: formData.userPassword,
        callNumber: formData.callNumber,
        email: formData.email,
        marketingOption: formData.marketingOption,
        typeFields: {
          companyName,
          position
        },
        registeredAt: new Date().toISOString()
      };

      const res = await fetch(`${API_URL}/api/marketer/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (res.ok) {
        const toUpload = [...bizFiles];
        setBizFiles([]);

        if (toUpload.length) {
          const fd = new FormData();
          fd.append('ownerId', data.user.id);
          toUpload.forEach(f => fd.append('files', f));
          uploadWithRetry(fd, `${API_URL}/api/marketer/biz-certi/upload`, {
            max: 3,
            baseDelay: 800,
            timeoutMs: 15000
          }).then(ok => {
            if (!ok) console.warn('사업자등록증 업로드 재시도 후 실패');
          });
        }

        setFormData({ userName: '', callNumber: '', email: '', userId: '', userPassword: '', marketingOption: false });
        setCompanyName(''); setPosition(''); setConfirmPassword('');
        setEmailVerified(false); setIdVerified(false); setRequiredAgreed(false);
        clearFieldErrors();
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'CompleteRegistration', { content_name: 'marketer' });
        }
        setIsSuccessModalOpen(true);
      } else {
        setMessage(`회원가입 실패: ${data.message || '오류가 발생했습니다.'}`);
      }
    } catch {
      setMessage('서버 요청 실패');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="marketer-regist-form">
      <p className="marketer-regist-form__section-title">기본 정보</p>

      <div className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">이름</label>
        <input
          ref={nameRef}
          name="userName"
          placeholder="실명을 입력해주세요"
          value={formData.userName}
          onChange={handleChange}
          disabled={loading}
          className={`marketer-regist-form__input${fieldErrors.userName ? ' marketer-regist-form__input--error' : ''}`}
        />
        {fieldErrors.userName && <span className="marketer-regist-form__field-error">{fieldErrors.userName}</span>}
      </div>

      <div className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">연락처</label>
        <input
          ref={callNumberRef}
          name="callNumber"
          placeholder="연락처 번호"
          value={formData.callNumber}
          onChange={handleChange}
          disabled={loading}
          className={`marketer-regist-form__input${fieldErrors.callNumber ? ' marketer-regist-form__input--error' : ''}`}
        />
        {fieldErrors.callNumber && <span className="marketer-regist-form__field-error">{fieldErrors.callNumber}</span>}
      </div>

      <div id="agency-section-email" className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">이메일</label>
        <EmailVerification
          formData={formData}
          setFormData={setFormData}
          emailVerified={emailVerified}
          setEmailVerified={setEmailVerified}
        />
        {fieldErrors.email && <span className="marketer-regist-form__field-error">{fieldErrors.email}</span>}
      </div>

      <div id="agency-section-id" className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">아이디</label>
        <IDVerification
          formData={formData}
          setFormData={setFormData}
          idVerified={idVerified}
          setIdVerified={setIdVerified}
          loading={loading}
          checkUrl={`${API_URL}/api/marketer/register/checkid`}
        />
        {fieldErrors.userId && <span className="marketer-regist-form__field-error">{fieldErrors.userId}</span>}
      </div>

      <div className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">비밀번호</label>
        <input
          ref={passwordRef}
          name="userPassword"
          type="password"
          autoComplete="new-password"
          placeholder="영문/숫자/특수기호 8~20자"
          value={formData.userPassword}
          onChange={(e) => {
            setFormData({ ...formData, userPassword: e.target.value });
            setFieldErrors(prev => ({ ...prev, userPassword: '' }));
          }}
          disabled={loading}
          minLength={8}
          maxLength={20}
          className={`marketer-regist-form__input${fieldErrors.userPassword ? ' marketer-regist-form__input--error' : ''}`}
        />
        {fieldErrors.userPassword
          ? <span className="marketer-regist-form__field-error">{fieldErrors.userPassword}</span>
          : formData.userPassword && !passRe.test(String(formData.userPassword)) && (
            <span className="marketer-regist-form__password-hint">영문/숫자(선택)/특수기호(선택) 조합 8~20자, 공백 불가</span>
          )
        }
      </div>

      <div className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">비밀번호 확인</label>
        <input
          ref={confirmPasswordRef}
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          placeholder="비밀번호를 한 번 더 입력해주세요"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
          }}
          disabled={loading}
          className={`marketer-regist-form__input${fieldErrors.confirmPassword ? ' marketer-regist-form__input--error' : ''}`}
        />
        {fieldErrors.confirmPassword
          ? <span className="marketer-regist-form__field-error">{fieldErrors.confirmPassword}</span>
          : confirmPassword && formData.userPassword !== confirmPassword && (
            <span className="marketer-regist-form__password-mismatch">비밀번호가 일치하지 않습니다.</span>
          )
        }
      </div>

      <p className="marketer-regist-form__section-title" style={{ marginTop: '8px' }}>소속 광고사 정보</p>

      <div className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">회사명</label>
        <input
          ref={companyNameRef}
          name="companyName"
          placeholder="광고사 회사명을 입력해주세요"
          value={companyName}
          onChange={(e) => {
            setCompanyName(e.target.value);
            setFieldErrors(prev => ({ ...prev, companyName: '' }));
          }}
          disabled={loading}
          className={`marketer-regist-form__input${fieldErrors.companyName ? ' marketer-regist-form__input--error' : ''}`}
        />
        {fieldErrors.companyName && <span className="marketer-regist-form__field-error">{fieldErrors.companyName}</span>}
      </div>

      <div className="marketer-regist-form__field">
        <label className="marketer-regist-form__label marketer-regist-form__label--required">직급</label>
        <input
          ref={positionRef}
          name="position"
          placeholder="직급을 입력해주세요"
          value={position}
          onChange={(e) => {
            setPosition(e.target.value);
            setFieldErrors(prev => ({ ...prev, position: '' }));
          }}
          disabled={loading}
          className={`marketer-regist-form__input${fieldErrors.position ? ' marketer-regist-form__input--error' : ''}`}
        />
        {fieldErrors.position && <span className="marketer-regist-form__field-error">{fieldErrors.position}</span>}
      </div>

      <div className="marketer-regist-form__section">
        <CertiUploader
          files={bizFiles}
          setFiles={setBizFiles}
          loading={loading}
          label="사업자 등록증 이미지를 첨부해주세요. (선택)"
        />
      </div>

      <div id="agency-section-terms" className="marketer-regist-form__section">
        <RegisterCheckbox
          requiredAgreed={requiredAgreed}
          setRequiredAgreed={setRequiredAgreed}
          formData={formData}
          setFormData={setFormData}
          loading={loading}
        />
        {fieldErrors.terms && <span className="marketer-regist-form__field-error">{fieldErrors.terms}</span>}
      </div>

      <button type="submit" disabled={loading} className="marketer-regist-form__submit">
        {loading ? '처리 중...' : '회원가입'}
      </button>
    </form>
  );
}

export default MarketerRegistAgencyForm;
